/**
 * 5 - Cut a Pipeline (03 §2): the armoured pumping station (time bomb only) inside a closed
 * ring of minefields thin enough for one grenade to open a lane; 2-4 automated minitanks
 * that wake when the ranger nears it; the pipe itself crossing the whole map, passable only
 * at its supports.
 */

import { buildRun } from '../obstacles';
import { mineDensity } from '../fieldworks';
import { NO_STAND } from '../builder';
import { FLAG } from '../grid';
import { MINES } from '../tuning';
import { checker, guard, insideRect, structure, targetsOf } from './helpers';
import type { ArchetypeSpec } from './types';

/** Narrower than this and the gap between two runs of pipe is a joint, not a support to crawl under. */
const SUPPORT_GAP = 2.5;

const tanksFor = (threat: number): number => 2 + (threat >= 5 ? 1 : 0) + (threat >= 7 ? 1 : 0);

/** A lair this close to the pickup is a bad neighbour for the Osprey. */
const LAIR_STAND_OFF = 25;

export const pipeline: ArchetypeSpec = {
  id: 'pipeline',
  name: 'CUT A PIPELINE',
  nativeBiome: 'temperate',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [32, 32], d: [32, 32] },
  sizeFor: () => ({ w: MINES.ringOuter * 2 + 4, d: MINES.ringOuter * 2 + 4 }),
  // South of the pipe: the pickup must not need a crawl under it.
  pickupDz: [6, 11],
  river: false,
  patrolLoop: 'outside',
  breach: { min: 1, max: MINES.ringOuter - MINES.ringInner + 2 },

  build(g) {
    const cx = Math.round((g.rect.x0 + g.rect.x1) / 2);
    const cz = Math.round((g.rect.z0 + g.rect.z1) / 2);
    const station = g.map.add('pumpStation', cx, cz, { target: true });

    const a = MINES.ringInner;
    const b = MINES.ringOuter;
    const density = mineDensity(g.ctx, MINES.ringDensity);
    // Four rectangles that tile a closed square ring: the ring is always marked - finding the lane is the puzzle.
    g.map.mineField({ x0: cx - b, z0: cz - b, x1: cx + b, z1: cz - a }, density, true);
    g.map.mineField({ x0: cx - b, z0: cz + a, x1: cx + b, z1: cz + b }, density, true);
    g.map.mineField({ x0: cx - b, z0: cz - a, x1: cx - a, z1: cz + a }, density, true);
    g.map.mineField({ x0: cx + a, z0: cz - a, x1: cx + b, z1: cz + a }, density, true);

    // The pipe: east-west across the whole map through the station, open only at two supports.
    const west = Math.max(5, cx - b - g.rng.range(6, 12));
    const east = Math.min(g.ctx.sizeX - 5, cx + b + g.rng.range(6, 12));
    const gaps = [{ x: cx, width: 6.8 }, { x: Math.round(west), width: 3 }, { x: Math.round(east), width: 3 }];
    buildRun(g.map, 'pipeline', cz, 0, g.ctx.sizeX, gaps);
    for (const s of gaps.slice(1)) g.map.mask.circle(s.x, cz, 3, FLAG.keep);

    const tanks = tanksFor(g.threat);
    const out = b + 9;
    const lairs: Array<[number, number]> = [
      [cx - 8, cz - out], [cx + out, cz - 6], [cx - out, cz - 6], [cx + 8, cz - out],
    ];
    // A tank waiting beside the pickup would make every extraction a gamble: those lairs are the last to be used.
    const { pickup } = g.ctx.plan;
    const byPickup = ([x, z]: [number, number]): number =>
      ((x - pickup.x) * (x - pickup.x) + (z - pickup.z) * (z - pickup.z) < LAIR_STAND_OFF * LAIR_STAND_OFF ? 1 : 0);
    lairs.sort((p, q) => byPickup(p) - byPickup(q));
    for (let i = 0; i < tanks; i++) {
      const lair = lairs[i];
      const lairX = lair ? Math.max(5, Math.min(g.ctx.sizeX - 5, lair[0])) : 0;
      const spot = lair ? g.map.nearestOpen(lairX, Math.max(4, lair[1]), 6, NO_STAND | FLAG.keep) : null;
      if (spot) g.map.spawn({ kind: 'minitank', x: spot[0], z: spot[1], facing: 0, dormantUntil: 'nearObjective' });
      else g.map.problems.push('pipeline: nowhere to hide a minitank');
    }
    const south = g.rect.z1;
    const yard = { x0: Math.max(6, cx - 24), z0: south + 1, x1: Math.min(g.ctx.sizeX - 6, cx + 24), z1: south + 9 };
    structure(g, 'hut', { within: yard, margin: 1.2 });
    structure(g, 'alarmPost', { within: yard, margin: 1 });
    guard(g, cx - 9, cz + b + 4);
    guard(g, cx + 9, cz + b + 4);
    if (g.threat >= 4) guard(g, cx, cz - b - 4, 3.14);
    return {
      objective: {
        kind: 'destroyTargets', label: 'CUT PIPELINE', targetPropIds: [station.id], minFraction: 1,
        rules: [{ type: 'recall', mode: 'normal' }],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('pipeline');
    const stations = targetsOf(def, 'pumpStation');
    check(
      stations.length === 1 && def.objective.targetPropIds.length === 1,
      'needs exactly one pumping station target',
    );
    const station = stations[0];
    if (station) {
      const crossed = probe.breach(station.id);
      check(crossed >= 1, 'the station can be reached without crossing mines: the ring is not closed');
      check(crossed <= 8, `the thinnest way through the mines is ${crossed} m: no grenade-clearable lane`);
    }
    const tanks = def.spawns.filter((s) => s.kind === 'minitank');
    check(tanks.length >= 2 && tanks.length <= 4, `needs 2-4 minitanks, has ${tanks.length}`);
    check(
      tanks.every((t) => t.dormantUntil === 'nearObjective'),
      'minitanks must be dormant until the ranger nears the station',
    );
    const pipes = def.props.filter((p) => p.kind === 'pipeline');
    const laid = pipes.reduce((sum, p) => sum + (p.length ?? 0), 0);
    check(laid >= def.sizeX * 0.7, 'the pipeline must run across the map');
    check(
      pipes.some((p) => p.x - (p.length ?? 0) / 2 < 1) && pipes.some((p) => p.x + (p.length ?? 0) / 2 > def.sizeX - 1),
      'the pipeline must reach both edges',
    );
    check(laid <= def.sizeX - 6, 'the pipeline needs gaps at its supports');
    // ROUND5 §2.6 (the marker audit), the half of it that is the generator's.
    //
    // "FIND OR MAKE A LANE THROUGH THE MINES": the ring is always sown `marked`, so its tell is there for
    // everyone without intel or a perk - finding the lane is the puzzle, not finding the minefield.
    const ringAt = (MINES.ringInner + MINES.ringOuter) / 2;
    if (station) {
      for (const [dx, dz] of [[ringAt, 0], [-ringAt, 0], [0, ringAt], [0, -ringAt]] as const) {
        const sown = def.mineFields.filter((f) => insideRect(f.rect, station.x + dx, station.z + dz));
        check(
          sown.length > 0 && sown.every((f) => f.marked),
          `the mine ring is not sown and marked ${dx}, ${dz} from the station`,
        );
      }
    }
    // "CRAWL UNDER IT AT THE MARKED SUPPORTS": the supports are the gaps BETWEEN the runs, which is what the
    // strip map draws them from - so there must be three of them and each must be wide enough to be one.
    const spans = pipes
      .map((p) => ({ x0: p.x - (p.length ?? 0) / 2, x1: p.x + (p.length ?? 0) / 2 }))
      .sort((a, b) => a.x0 - b.x0);
    const gaps: Array<{ x: number; width: number }> = [];
    for (let i = 1; i < spans.length; i++) {
      const [prev, next] = [spans[i - 1], spans[i]];
      if (!prev || !next) continue;
      const width = next.x0 - prev.x1;
      if (width > 0.5) gaps.push({ x: (prev.x1 + next.x0) / 2, width });
    }
    check(gaps.length === 3, `the pipe needs its three supports to crawl under, has ${gaps.length}`);
    check(
      gaps.every((g) => g.width >= SUPPORT_GAP),
      `a support is only ${Math.min(...gaps.map((g) => g.width)).toFixed(1)} m wide: nobody crawls through that`,
    );
    if (station) {
      check(gaps.some((g) => Math.abs(g.x - station.x) < 1), 'the pipe must be open where it meets the station');
    }
    return out;
  },
};
